import { useState, useRef, useEffect } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    QrCode,
    X,
    Upload,
    Download,
    Camera,
    CheckCircle2,
    AlertCircle,
    Copy,
    Smartphone,
} from "lucide-react";
import type { Task, DailyStats } from "@/types";

interface DataTransferModalProps {
    isOpen: boolean;
    onClose: () => void;
    tasks: Task[];
    dailyStats: Record<string, DailyStats>;
    onImport: (tasks: Task[], dailyStats: Record<string, DailyStats>) => void;
}

interface ExportData {
    version: 1;
    exportedAt: string;
    tasks: Task[];
    dailyStats: Record<string, DailyStats>;
}

type TabType = "export" | "import";

export function DataTransferModal({
    isOpen,
    onClose,
    tasks,
    dailyStats,
    onImport,
}: DataTransferModalProps) {
    const [activeTab, setActiveTab] = useState<TabType>("export");
    const [importStatus, setImportStatus] = useState<
        "idle" | "success" | "error"
    >("idle");
    const [importMessage, setImportMessage] = useState("");
    const [scanning, setScanning] = useState(false);
    const [cameraError, setCameraError] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const scanIntervalRef = useRef<number | null>(null);

    // Generate export data
    const generateExportData = (): ExportData => {
        return {
            version: 1,
            exportedAt: new Date().toISOString(),
            tasks: tasks,
            dailyStats: dailyStats,
        };
    };

    // Compress data for QR code (using base64)
    const compressData = (data: ExportData): string => {
        const jsonStr = JSON.stringify(data);
        // Use base64 encoding
        return btoa(encodeURIComponent(jsonStr));
    };

    // Decompress data from QR code
    const decompressData = (compressed: string): ExportData | null => {
        try {
            const jsonStr = decodeURIComponent(atob(compressed));
            return JSON.parse(jsonStr);
        } catch {
            return null;
        }
    };

    // Generate QR code URL
    const getQRCodeUrl = () => {
        const data = generateExportData();
        const compressed = compressData(data);
        // QR codes have a limit, so we'll use a chunked approach for large data
        // For MVP, we'll just encode what we can
        const maxLength = 2000; // Safe limit for QR codes

        if (compressed.length > maxLength) {
            // For large data, we'll need to show a warning or use alternative method
            return null;
        }

        return `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(
            compressed
        )}`;
    };

    // Copy data to clipboard as JSON
    const copyToClipboard = async () => {
        const data = generateExportData();
        const compressed = compressData(data);
        await navigator.clipboard.writeText(compressed);
        setImportMessage("Data copied to clipboard!");
        setImportStatus("success");
        setTimeout(() => setImportStatus("idle"), 2000);
    };

    // Import from clipboard
    const importFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();
            processImportData(text);
        } catch {
            setImportStatus("error");
            setImportMessage(
                "Failed to read clipboard. Please paste the data manually."
            );
        }
    };

    // Process imported data
    const processImportData = (compressed: string) => {
        const data = decompressData(compressed.trim());

        if (!data || !data.tasks) {
            setImportStatus("error");
            setImportMessage("Invalid data format. Please try again.");
            return;
        }

        // Check if there's already an active task in current tasks
        const currentActiveTask = tasks.find((t) => t.status === "active");

        // Convert date strings to Date objects and handle active task conflicts
        const processedTasks: Task[] = data.tasks.map((t: any) => {
            const task: Task = {
                ...t,
                createdAt: new Date(t.createdAt),
                completedAt: t.completedAt
                    ? new Date(t.completedAt)
                    : undefined,
                activeStartedAt: t.activeStartedAt
                    ? new Date(t.activeStartedAt)
                    : undefined,
                originalDate: t.originalDate
                    ? new Date(t.originalDate)
                    : undefined,
            };

            // If there's already an active task and this imported task is also active,
            // pause the imported task to avoid conflicts
            if (currentActiveTask && task.status === "active") {
                // Calculate elapsed time before pausing
                if (task.activeStartedAt) {
                    const now = new Date();
                    const additionalSeconds = Math.floor(
                        (now.getTime() - task.activeStartedAt.getTime()) / 1000
                    );
                    task.elapsedSeconds =
                        task.elapsedSeconds + Math.max(0, additionalSeconds);
                }
                task.status = "pending";
                task.activeStartedAt = undefined;
            }

            // Also handle comments timestamps
            if (task.comments) {
                task.comments = task.comments.map((c: any) => ({
                    ...c,
                    timestamp: new Date(c.timestamp),
                }));
            }

            return task;
        });

        // Merge tasks - avoid duplicates by ID
        const existingIds = new Set(tasks.map((t) => t.id));
        const newTasks = processedTasks.filter((t) => !existingIds.has(t.id));

        // Merge daily stats
        const mergedStats = { ...dailyStats };
        for (const [date, stats] of Object.entries(data.dailyStats || {})) {
            if (!mergedStats[date]) {
                mergedStats[date] = stats;
            } else {
                // Merge stats for same date (take higher values)
                mergedStats[date] = {
                    ...mergedStats[date],
                    totalTasks: Math.max(
                        mergedStats[date].totalTasks,
                        stats.totalTasks
                    ),
                    completedTasks: Math.max(
                        mergedStats[date].completedTasks,
                        stats.completedTasks
                    ),
                    totalWorkedSeconds: Math.max(
                        mergedStats[date].totalWorkedSeconds,
                        stats.totalWorkedSeconds
                    ),
                    totalEstimatedMinutes: Math.max(
                        mergedStats[date].totalEstimatedMinutes,
                        stats.totalEstimatedMinutes
                    ),
                };
            }
        }

        onImport([...tasks, ...newTasks], mergedStats);

        setImportStatus("success");
        setImportMessage(
            `Imported ${newTasks.length} new task${
                newTasks.length !== 1 ? "s" : ""
            }!`
        );

        // Close after success
        setTimeout(() => {
            onClose();
            setImportStatus("idle");
        }, 1500);
    };

    // Start camera for QR scanning
    const startScanning = async () => {
        try {
            setCameraError(null);
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "environment" },
            });

            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
                setScanning(true);

                // Start scanning interval
                scanIntervalRef.current = window.setInterval(() => {
                    scanQRCode();
                }, 500);
            }
        } catch (err) {
            setCameraError(
                "Could not access camera. Please grant permission or use the paste option."
            );
            console.error("Camera error:", err);
        }
    };

    // Stop camera
    const stopScanning = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (scanIntervalRef.current) {
            clearInterval(scanIntervalRef.current);
            scanIntervalRef.current = null;
        }
        setScanning(false);
    };

    // Scan QR code from video
    const scanQRCode = async () => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        if (!ctx || video.readyState !== video.HAVE_ENOUGH_DATA) return;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);

        // Use BarcodeDetector API if available
        if ("BarcodeDetector" in window) {
            try {
                // @ts-ignore - BarcodeDetector is not in TypeScript types yet
                const detector = new BarcodeDetector({ formats: ["qr_code"] });
                const barcodes = await detector.detect(canvas);

                if (barcodes.length > 0) {
                    stopScanning();
                    processImportData(barcodes[0].rawValue);
                }
            } catch (err) {
                // BarcodeDetector failed, continue scanning
            }
        }
    };

    // Cleanup on unmount or close
    useEffect(() => {
        return () => {
            stopScanning();
        };
    }, []);

    // Stop scanning when modal closes
    useEffect(() => {
        if (!isOpen) {
            stopScanning();
            setImportStatus("idle");
            setImportMessage("");
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const qrCodeUrl = getQRCodeUrl();
    const dataSize = compressData(generateExportData()).length;
    const isDataTooLarge = dataSize > 2000;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => {
                    stopScanning();
                    onClose();
                }}
            />

            {/* Modal */}
            <Card className="relative z-10 w-full max-w-md border-0 shadow-2xl max-h-[90vh] overflow-auto">
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Smartphone className="w-5 h-5 text-violet-500" />
                            Transfer Data
                        </CardTitle>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                                stopScanning();
                                onClose();
                            }}
                            className="h-8 w-8"
                        >
                            <X className="w-4 h-4" />
                        </Button>
                    </div>
                    <CardDescription>
                        Sync your tasks between devices
                    </CardDescription>
                </CardHeader>

                {/* Tabs */}
                <div className="flex border-b mx-4">
                    <button
                        className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === "export"
                                ? "border-violet-500 text-violet-600"
                                : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}
                        onClick={() => {
                            setActiveTab("export");
                            stopScanning();
                        }}
                    >
                        <Download className="w-4 h-4 inline mr-1" />
                        Export
                    </button>
                    <button
                        className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === "import"
                                ? "border-violet-500 text-violet-600"
                                : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}
                        onClick={() => setActiveTab("import")}
                    >
                        <Upload className="w-4 h-4 inline mr-1" />
                        Import
                    </button>
                </div>

                <CardContent className="pt-4">
                    {activeTab === "export" ? (
                        <div className="space-y-4">
                            {/* Stats */}
                            <div className="flex gap-2 justify-center">
                                <Badge variant="secondary">
                                    {tasks.length} tasks
                                </Badge>
                                <Badge variant="secondary">
                                    {Object.keys(dailyStats).length} days of
                                    stats
                                </Badge>
                            </div>

                            {/* QR Code */}
                            {qrCodeUrl && !isDataTooLarge ? (
                                <div className="flex flex-col items-center gap-3">
                                    <div className="p-4 bg-white rounded-xl shadow-inner">
                                        <img
                                            src={qrCodeUrl}
                                            alt="QR Code with task data"
                                            className="w-56 h-56"
                                        />
                                    </div>
                                    <p className="text-xs text-muted-foreground text-center">
                                        Scan this QR code on another device to
                                        import your tasks
                                    </p>
                                </div>
                            ) : (
                                <div className="text-center py-4 px-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                                    <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                                    <p className="text-sm text-amber-700 dark:text-amber-300">
                                        Data too large for QR code (
                                        {Math.round(dataSize / 1024)}KB).
                                        <br />
                                        Use the copy button below instead.
                                    </p>
                                </div>
                            )}

                            {/* Copy button */}
                            <Button
                                className="w-full"
                                variant="outline"
                                onClick={copyToClipboard}
                            >
                                <Copy className="w-4 h-4 mr-2" />
                                Copy Data to Clipboard
                            </Button>

                            {importStatus === "success" && importMessage && (
                                <p className="text-sm text-green-600 text-center flex items-center justify-center gap-1">
                                    <CheckCircle2 className="w-4 h-4" />
                                    {importMessage}
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Camera Scanner */}
                            {scanning ? (
                                <div className="space-y-3">
                                    <div className="relative aspect-square bg-black rounded-lg overflow-hidden">
                                        <video
                                            ref={videoRef}
                                            className="w-full h-full object-cover"
                                            playsInline
                                            muted
                                        />
                                        <div className="absolute inset-0 border-2 border-violet-500 rounded-lg pointer-events-none">
                                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 border-2 border-white/50 rounded-lg" />
                                        </div>
                                    </div>
                                    <canvas
                                        ref={canvasRef}
                                        className="hidden"
                                    />
                                    <Button
                                        className="w-full"
                                        variant="outline"
                                        onClick={stopScanning}
                                    >
                                        Stop Scanning
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <Button
                                        className="w-full"
                                        onClick={startScanning}
                                    >
                                        <Camera className="w-4 h-4 mr-2" />
                                        Scan QR Code
                                    </Button>

                                    {cameraError && (
                                        <p className="text-sm text-amber-600 text-center">
                                            {cameraError}
                                        </p>
                                    )}

                                    <div className="relative">
                                        <div className="absolute inset-0 flex items-center">
                                            <span className="w-full border-t" />
                                        </div>
                                        <div className="relative flex justify-center text-xs uppercase">
                                            <span className="bg-background px-2 text-muted-foreground">
                                                or
                                            </span>
                                        </div>
                                    </div>

                                    <Button
                                        className="w-full"
                                        variant="outline"
                                        onClick={importFromClipboard}
                                    >
                                        <Upload className="w-4 h-4 mr-2" />
                                        Paste from Clipboard
                                    </Button>

                                    <p className="text-xs text-muted-foreground text-center">
                                        Copy the data from the export screen on
                                        your other device, then paste it here.
                                    </p>
                                </div>
                            )}

                            {/* Status messages */}
                            {importStatus === "success" && (
                                <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg text-center">
                                    <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto mb-1" />
                                    <p className="text-sm text-green-700 dark:text-green-300">
                                        {importMessage}
                                    </p>
                                </div>
                            )}

                            {importStatus === "error" && (
                                <div className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg text-center">
                                    <AlertCircle className="w-6 h-6 text-red-500 mx-auto mb-1" />
                                    <p className="text-sm text-red-700 dark:text-red-300">
                                        {importMessage}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
